import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import PropTypes from 'prop-types';

import styles from './select.module.css';

function Select({
    disabled,
    error,
    errorMsg,
    id,
    isMisfits,
    label,
    labelId,
    menuItems,
    placeholder,
    required,
    value,
    width,
    ...rest
}) {
    // Without altering MUI's built-in focus handling, manually control
    // the visible focus styling in order to adhere to our designs.
    const [focusedMenuItem, setFocusedMenuItem] = useState(null);
    const [initialFocus, setInitialFocus] = useState(true);

    const handleClose = () => {
        setFocusedMenuItem(null);
        setInitialFocus(true);
    };

    const handleFocus = (focusedValue) => {
        if (initialFocus) {
            setInitialFocus(false);
        } else {
            setFocusedMenuItem(focusedValue);
        }
    };

    const themedMenuClass = isMisfits ? 'misfits-select-dropdown-menu' : 'imperfect-select-dropdown-menu';
    const menuClassName = `${styles[themedMenuClass]} ${styles['select-dropdown-menu']}`;

    return (
        <FormControl
            className={styles['select-dropdown']}
            style={{ width }}
            required={required}
            error={error}
            disabled={disabled}
        >
            <InputLabel id={labelId} shrink>
                {label}
            </InputLabel>
            <MuiSelect
                labelId={labelId}
                label={label}
                id={id}
                value={value}
                IconComponent={ChevronDownIcon}
                MenuProps={{ PaperProps: { className: menuClassName }, style: { zIndex: 5001 } }}
                onClose={handleClose}
                native={false}
                displayEmpty
                renderValue={(selectedValue) =>
                    selectedValue ? (
                        menuItems.find((item) => item.value === selectedValue)?.itemCopy
                    ) : (
                        <span>{placeholder}</span>
                    )
                }
                {...rest}
            >
                {menuItems.map((item, index) => (
                    <MenuItem
                        key={item.itemCopy}
                        disableRipple
                        value={item.value}
                        disabled={!!item.disabled}
                        divider={index < menuItems.length - 1}
                        onFocus={() => handleFocus(item.value)}
                        focusVisibleClassName={focusedMenuItem === item.value ? 'controlled-focus' : ''}
                    >
                        <span>{item.itemCopy}</span>
                    </MenuItem>
                ))}
            </MuiSelect>
            {error && errorMsg !== '' && <p className="text-body-md mt-5 font-grotesk-bold text-error">{errorMsg}</p>}
        </FormControl>
    );
}

export default Select;

Select.propTypes = {
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    errorMsg: PropTypes.string,
    id: PropTypes.string.isRequired,
    isMisfits: PropTypes.bool,
    label: PropTypes.string.isRequired,
    labelId: PropTypes.string.isRequired,
    menuItems: PropTypes.arrayOf(
        PropTypes.shape({
            disabled: PropTypes.bool,
            itemCopy: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
        })
    ).isRequired,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    width: PropTypes.string,
};

Select.defaultProps = {
    disabled: false,
    error: false,
    errorMsg: '',
    isMisfits: true,
    placeholder: 'Select',
    required: false,
    width: '100%',
};
